<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import VolumesOverlay from "components/VolumesOverlay.vue";
import DropdownList from "components/DropdownList.vue";
import LoginModal from "components/LoginModal.vue";

import {loginSession, useLoginStore, useVolumesStore} from '../store';

import logoImage from '../CaveLogo-clear.png';

const login = useLoginStore();

window.addEventListener("middleauthlogin", () => {
  login.update();
});

login.update();

const validLogins = computed(() => login.sessions.filter(x => x.status === undefined));
const invalidLogins = computed(() => login.sessions.filter(x => x.status !== undefined));

const {volumes} = useVolumesStore();

onMounted(() => {
  (document.querySelector('.ng-extend-logo > a > img')! as HTMLImageElement).src = logoImage;
});

const showModal = ref(false);

function logout(session: loginSession) {
  login.logout(session);
}

// ── Toolbar icon definitions ──────────────────────────────────
interface ToolbarIcon {
  id: string;
  emoji: string;
  svg?: string;
  label: string;
  action: () => void;
}

// Branch-style SVG icons for split & merge
const SPLIT_SVG = `<svg viewBox="0 0 16 16" fill="none" style="width:16px;height:16px;vertical-align:middle"><path d="M8 3v2a4 4 0 0 1-4 4H4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M8 3v2a4 4 0 0 0 4 4h0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="8" cy="2.5" r="1.5" fill="currentColor"/><circle cx="4" cy="13" r="1.5" fill="currentColor"/><circle cx="12" cy="13" r="1.5" fill="currentColor"/><path d="M4 9v4M12 9v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
const MERGE_SVG = `<svg viewBox="0 0 16 16" fill="none" style="width:16px;height:16px;vertical-align:middle"><path d="M4 3v4a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="4" cy="2.5" r="1.5" fill="currentColor"/><circle cx="12" cy="2.5" r="1.5" fill="currentColor"/><circle cx="8" cy="13" r="1.5" fill="currentColor"/><path d="M8 11v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;

const toolbarDefs = computed<ToolbarIcon[]>(() => [
  { id: 'split', emoji: '', svg: SPLIT_SVG, label: 'Split Mode (C)', action: () => activateTool('multicut') },
  { id: 'merge', emoji: '', svg: MERGE_SVG, label: 'Merge Mode (M)', action: () => activateTool('merge') },
]);

function activateTool(toolType: 'multicut' | 'merge') {
  const viewer: any = (window as any)['viewer'];
  if (!viewer) return;

  // 1. Select the segmentation layer (required for tool keybindings)
  try {
    const segLayer = viewer.layerManager?.managedLayers?.find(
      (x: any) => x.layer?.constructor?.name?.includes('Segmentation'),
    );
    if (segLayer) {
      viewer.selectedLayer.layer = segLayer;
      viewer.selectedLayer.visible = true;
    }
  } catch { /* non-critical */ }

  // 2. Dispatch keyboard shortcut to the viewer element where ng binds handlers
  const key = toolType === 'multicut' ? 'c' : 'm';
  const eventInit: KeyboardEventInit = {
    key, code: key === 'c' ? 'KeyC' : 'KeyM',
    bubbles: true, cancelable: true,
  };
  const targets = [
    viewer.element,
    viewer.display?.container,
    document.getElementById('neuroglancer-container'),
  ].filter(Boolean);
  for (const el of targets) {
    if (el instanceof HTMLElement) el.focus();
    el.dispatchEvent(new KeyboardEvent('keydown', eventInit));
  }
}

</script>

<template>
  <login-modal />
  <volumes-overlay v-visible="showModal" @hide="showModal = false" />
  <div id="extensionBar">
    <div class="ng-extend-logo">
      <a href="https://h01-release.storage.googleapis.com/explore.html" target="_blank">
        <img src="insert-logo" title="Cave Explorer">
      </a>
    </div>
    <div id="insertNGTopBar" class="flex-fill"></div>
    <button v-if="volumes.length" @click="showModal = true">Volumes ({{ volumes.length }})</button>
    <div class="nge-toolbar-icons" v-if="login.sessions.length > 0">
      <button
        v-for="icon in toolbarDefs"
        :key="icon.id"
        class="nge-icon-btn"
        :title="icon.label"
        @click="icon.action()"
      ><span v-if="icon.svg" v-html="icon.svg"></span><template v-else>{{ icon.emoji }}</template></button>
    </div>

    <template v-if="login.sessions.length > 0">
      <dropdown-list dropdown-group="extension-bar-right" id="loginsDropdown" class="rightMost">
          <template #buttonTitle>Logins ({{ login.sessions.length }})</template>
          <template #listItems>
            <li v-for="session of validLogins">
              <div class="loginRow">
                <div class="loginData">
                  <div>{{ session.email }}</div>
                  <div>{{ session.hostname }}</div>
                </div>
                <div class="logoutButton" @click="logout(session)"><span>Logout</span></div>
              </div>
            </li>
            <li v-for="session in invalidLogins">
              <div class="loginRow">
                <div class="loginData expired">
                  <div>{{ session.hostname }} - Expired</div>
                </div>
                <div class="logoutButton" @click="logout(session)"><span>Delete</span></div>
              </div>
            </li>
          </template>
        </dropdown-list>
    </template>
  </div>
</template>

<style>
.dropdownList:last-child .dropdownMenu {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

#extensionBar button {
  font-size: 10pt;
}

#insertNGTopBar > div {
  width: 100%;
}

#extensionBar {
  display: flex;
  height: 40px;
  align-items: center;
  background-color: var(--color-dark-bg);
  z-index: 30;
}

#extensionBar > * {
  height: 100%;
  display: flex;
  align-items: center;
}

#loginsDropdown li.none {
  opacity: 0.5;
  padding: 0 10px;
}

#loginsDropdown li > div:last-child {
  border-bottom: none;
}

#loginsDropdown li > div {
  display: grid;
  grid-template-columns: auto min-content;
  border-bottom: 1px solid #4a4a4a;
}

#loginsDropdown .loginData {
  display: grid;
  white-space: nowrap;
  padding: 10px;
}

#loginsDropdown .logoutButton {
  display: grid;
  align-content: center;
  justify-content: center;
  padding-left: 10px;
  padding-right: 10px;
  opacity: 0;
}

#loginsDropdown .loginRow:hover .logoutButton {
 opacity: 0.25;
}

#loginsDropdown .loginRow:hover .logoutButton:hover {
  opacity: 1;
  background-color: #db4437;
  cursor: pointer;
}

#loginsDropdown li.header {
  padding: 5px;
  background-color: #ffffff1c;
}

#loginsDropdown .loginData.expired {
  opacity: 0.5;
}

.ng-extend-logo > a, .ng-extend-logo > a > img {
  height: 100%;
}

/* ── Toolbar icon group ── */
.nge-toolbar-icons {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  height: 100%;
}

.nge-icon-btn {
  font-size: 14px;
  width: 32px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s, background 0.15s;
}
.nge-icon-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.06);
}
</style>
